import { QueryClient } from '@tanstack/react-query';
import { signTypedData as _signTypedData } from '@wagmi/core';
import { isAfter } from 'date-fns';
import { Address, Hex, isAddressEqual } from 'viem';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { Config } from 'wagmi';
import { SignatureType, SIGNER_EXPIRY_BUFFER_MS } from '../constants';
import { SchemaDomainTypeDto, SchemaSignerDto } from '../generated/apiTypes';
import { activeSignerQueryParams } from '../hooks/useActiveSigner';
import { getSpecificSignatureType, SignTypedDataTypes } from '../hooks/useDataForSigning';
import { LinkSignerMessage } from '../types/generated';
import { fetchClient as _fetchClient, dataOrThrow } from './apiClient';
import { getNonceInNanoseconds, nowInSeconds } from './time';
import { PrettyMerge } from './tsUtilityTypes';
import { wait } from './wait';

interface LinkSignerParams {
  address: Address;
  subaccountId: string;
  subaccountName: Hex;
  config: Config;
  domain: SchemaDomainTypeDto;
  signedTypedDataTypes: SignTypedDataTypes;
}
export const linkSigner = async (
  { address, subaccountId, subaccountName, config, domain, signedTypedDataTypes }: LinkSignerParams,
  deps = { fetchClient: _fetchClient, signTypedData: _signTypedData }
) => {
  const nonce = getNonceInNanoseconds();
  const signedAt = nowInSeconds();
  const privateKey = generatePrivateKey();
  const account = privateKeyToAccount(privateKey);

  const message: Omit<LinkSignerMessage, 'signature'> = {
    sender: address,
    signer: account.address,
    subaccount: subaccountName,
    signedAt: BigInt(signedAt),
    nonce,
  };

  const signature = await deps.signTypedData(config, {
    domain,
    types: getSpecificSignatureType(signedTypedDataTypes, SignatureType.LINK_SIGNER),
    primaryType: SignatureType.LINK_SIGNER,
    message,
  });

  const signerSignature = await account.signTypedData({
    domain,
    types: getSpecificSignatureType(signedTypedDataTypes, SignatureType.LINK_SIGNER),
    primaryType: SignatureType.LINK_SIGNER,
    message,
  });

  await deps.fetchClient
    .POST('/v1/linked-signer/link', {
      body: {
        data: {
          subaccountId: subaccountId,
          subaccount: subaccountName,
          sender: message.sender,
          signer: message.signer,
          nonce: message.nonce.toString(),
          signedAt: Number(message.signedAt),
        },
        signature,
        signerSignature,
      },
    })
    .then(dataOrThrow);

  return { signerPrivateKey: privateKey, signerAddress: account.address };
};

interface RevokeLinkedSignerParams extends LinkSignerParams {
  signerAddress: Address;
}

export const revokeSigner = async (
  {
    address,
    subaccountName,
    subaccountId,
    config,
    domain,
    signedTypedDataTypes,
    signerAddress,
  }: RevokeLinkedSignerParams,
  deps = { fetchClient: _fetchClient, signTypedData: _signTypedData }
) => {
  const nonce = getNonceInNanoseconds();
  const signedAt = nowInSeconds();
  const message: Omit<LinkSignerMessage, 'signature'> = {
    sender: address,
    signer: signerAddress,
    subaccount: subaccountName,
    nonce,
    signedAt: BigInt(signedAt),
  };

  const signature = await deps.signTypedData(config, {
    domain,
    types: getSpecificSignatureType(signedTypedDataTypes, SignatureType.REVOKE_LINKED_SIGNER),
    primaryType: SignatureType.REVOKE_LINKED_SIGNER,
    message,
  });
  await deps.fetchClient
    .DELETE('/v1/linked-signer/revoke', {
      body: {
        data: {
          subaccountId: subaccountId,
          subaccount: subaccountName,
          sender: message.sender,
          signer: message.signer,
          nonce: message.nonce.toString(),
          signedAt: Number(signedAt),
        },
        signature,
      },
    })
    .then(dataOrThrow);
};

const SIGNER_POLL_INTERVAL = 3000;

export type PollSignerParams = PrettyMerge<LinkSignerParams, { signerAddress: Address }>;
export const pollSignerStatus = async ({
  queryClient,
  params,
  isRevoke,
}: {
  queryClient: QueryClient;
  params: PollSignerParams;
  isRevoke: boolean;
}): Promise<boolean> => {
  const activeQueryParams = activeSignerQueryParams(params.subaccountId);

  const signers = await queryClient.fetchQuery(activeQueryParams);
  const signer = signers?.data.find((linkedSigner) =>
    isAddressEqual(linkedSigner.signer, params.signerAddress)
  );

  const done = isRevoke ? !signer : Boolean(signer);
  if (done) {
    return true;
  }

  await wait(SIGNER_POLL_INTERVAL);
  return pollSignerStatus({ queryClient, params, isRevoke });
};

export const isExpired = (signer: SchemaSignerDto | undefined) => {
  if (!signer) return false;
  // Add a buffer to account for network delays and ensure requests complete
  const expiryWithBuffer = new Date(new Date(signer.expiresAt).getTime() - SIGNER_EXPIRY_BUFFER_MS);
  return isAfter(new Date(), expiryWithBuffer);
};
